<script setup lang="ts">
import type { WalletType } from '~/composables/usePlayWithBonus';
import { resolveCrashGameName } from '~/utils/apostatudo/crash-game-name';

const {
  closePreview,
  selectedGame,
  isPreviewOpen,
  isEnrichingGame,
  rememberAsLastPreview,
} = useGameMobilePreview();

const router = useRouter();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const casinoStore = useCasinoStore();

const currentGameId = computed(() => {
  const id = selectedGame.value?.id;

  return id ? Number(id) : 0;
});

const gameLink = computed(() => {
  if (!selectedGame.value?.slug) return '/';

  return `/games/${selectedGame.value.slug.toLowerCase()}`;
});

// Pre-jogo dos crash games (ENG-833). Quem diz se este jogo tem historico de
// velas e o resolver — hoje so Aviator e Aviator 2 tem coletor no core. null =
// jogo comum, e nenhum bloco de crash entra na tela nem faz request.
const crashGameName = computed(() =>
  resolveCrashGameName(selectedGame.value?.slug),
);

// Jogar com Bonus: quando ha escolha real a fazer, ela acontece AQUI mesmo —
// um toque a menos que mandar o jogador pro gate na rota do jogo. A carteira
// escolhida viaja no `?wallet=`, que e o que o GameIframe le pra montar o
// launch. Sem escolha (jogo nao elegivel ou sem saldo bonus), o botao continua
// sendo um so e nada muda no fluxo.
const {
  canUseBonus,
  isWalletChoiceLoading,
  ensureWalletLoaded,
  ensureRolloverLoaded,
  walletBonusFormatted,
  rollover,
  bonusExpiry,
  WALLET_TYPE,
  WALLET_TYPE_QUERY,
} = usePlayWithBonus();

const showBonusOption = computed<boolean>(() => {
  return canUseBonus(selectedGame.value);
});

// Trava o toque enquanto falta resposta para decidir a carteira: saldo de
// bonus (wallet) e elegibilidade do jogo (enriquecimento). A escolha e imutavel
// pela sessao, entao um toque adiantado nao mostra um estado velho por um
// instante — abre o jogo na carteira errada sem volta.
const isChoiceLoading = computed<boolean>(() => {
  return isWalletChoiceLoading.value || isEnrichingGame.value;
});

function playGame(walletType: WalletType = WALLET_TYPE.REAL) {
  const target = gameLink.value;
  const gameToRemember = selectedGame.value;

  closePreview();
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }
  if (gameToRemember) rememberAsLastPreview(gameToRemember);
  casinoStore.isGameModalOpen = true;

  // Sai SEMPRE com `?wallet=` — mesmo quando nao houve o que escolher. A
  // escolha do mobile acontece aqui, e o gate do GameIframe so aparece na
  // ausencia do parametro: navegar sem ele deixaria o jogador ver a escolha
  // duas vezes se a carteira terminasse de carregar no meio do caminho (o
  // preview mostraria um botao so, e a tela do jogo mostraria os dois).
  router.push({ path: target, query: { [WALLET_TYPE_QUERY]: walletType } });
}

// Fetches independentes: cada um tem seu próprio loading flag. Assim
// estatísticas e mais-pagou trocam de skeleton pro real cada um no seu
// tempo, sem esperar o outro.
const { fetchGameStatisticsBySlug } = useGamesStatistics();
const { getGamesFromCore } = useCasino();

const statistics = ref<GameStatisticsByPeriod | null>(null);
const mostPaidGames = ref<Game[] | null>(null);
const isStatisticsLoading = ref(false);
const isMostPaidLoading = ref(false);

async function loadStatistics(slug: string) {
  isStatisticsLoading.value = true;
  statistics.value = null;
  try {
    statistics.value = (await fetchGameStatisticsBySlug(slug)) || null;
  } catch {
    statistics.value = null;
  } finally {
    isStatisticsLoading.value = false;
  }
}

async function loadMostPaid() {
  // Resultado nao depende do jogo selecionado — busca uma vez e reaproveita
  // enquanto o componente estiver montado. Trocar de jogo nao redispara.
  if (mostPaidGames.value && mostPaidGames.value.length > 0) return;
  if (isMostPaidLoading.value) return;

  isMostPaidLoading.value = true;
  try {
    const res = await getGamesFromCore([
      ['sort_by', 'turnover'],
      ['sort_dir', 'desc'],
      ['per_page', '10'],
      ['page', 1],
    ]);

    mostPaidGames.value = (res?.data || []) as unknown as Game[];
  } catch {
    mostPaidGames.value = [];
  } finally {
    isMostPaidLoading.value = false;
  }
}

watch(
  () => selectedGame.value?.slug,
  (slug) => {
    if (!slug) return;
    loadStatistics(slug.toLowerCase());
    loadMostPaid();
  },
  { immediate: true },
);

// Lock body scroll while open. Pelo composable compartilhado, e nao na mao: o
// pull-to-refresh do app nativo (usePullToRefresh) decide se o gesto vale
// lendo o contador de `useBodyScrollLock`, entao um lock manual travava o
// scroll visualmente mas deixava o puxao pra baixo recarregar a pagina por
// cima do preview. O contador tambem evita que fechar este sheet destrave o
// body de um modal ainda aberto por baixo.
useBodyScrollLock(isPreviewOpen);

// Garante a carteira antes de o sheet decidir se mostra um botao ou dois. O
// `showBonusOption` e reativo, entao sem isto o botao de bonus so apareceria
// depois que algum outro ponto do app buscasse o saldo — aparecendo do nada
// debaixo do dedo do jogador, ou nunca, se nada mais buscar.
watch(isPreviewOpen, (open) => {
  if (!open) return;

  ensureWalletLoaded();
  ensureRolloverLoaded();
});

onUnmounted(() => {
  stopWatchingActions();
});

// Trava o rubber-band do iOS: bloqueia touchmove quando o usuário tenta
// scrollar além do início/fim do .mgp-stack. Sem isso, o iOS Safari "estica"
// o conteúdo (efeito chiclete) mesmo com overscroll-behavior: none.
const stackRef = ref<HTMLElement | null>(null);
let touchStartY = 0;
let touchStartX = 0;

// Barra flutuante com o JOGAR. Só nos crash games: neles o painel de pré-jogo
// (velas, janelas, frequências) é longo o bastante pra empurrar o botão pra
// fora da tela — num jogo comum o botão continua a um scroll curto do topo e
// uma barra fixa só roubaria altura.
//
// Quem decide a hora é o PRÓPRIO botão saindo de vista, não um limiar em
// pixels: a altura do bloco acima dele muda com o que o jogador tem na conta
// (botão de bônus, saldo, rollover, validade, tags), então qualquer número
// fixo acertaria num caso e erraria nos outros — mostrando a barra com o botão
// original ainda na tela, ou só depois dele ter sumido há muito.
const actionsRef = ref<HTMLElement | null>(null);
const isPlayOutOfView = ref(false);

let actionsObserver: IntersectionObserver | null = null;

function stopWatchingActions() {
  actionsObserver?.disconnect();
  actionsObserver = null;
}

function watchActions() {
  stopWatchingActions();

  const target = actionsRef.value;
  const root = stackRef.value;

  if (!target || !root || typeof IntersectionObserver === 'undefined') return;

  // root = o .mgp-stack, que é quem rola. Com o root padrão (viewport) o
  // recorte seria o da tela inteira, e não o da área visível do sheet.
  actionsObserver = new IntersectionObserver(
    ([entry]) => {
      isPlayOutOfView.value = entry ? !entry.isIntersecting : false;
    },
    { root, threshold: 0 },
  );

  actionsObserver.observe(target);
}

const showFloatingPlay = computed(
  () => Boolean(crashGameName.value) && isPlayOutOfView.value,
);

function onTouchStart(e: TouchEvent) {
  touchStartY = e.touches[0]?.clientY ?? 0;
  touchStartX = e.touches[0]?.clientX ?? 0;
}

function onTouchMove(e: TouchEvent) {
  const el = stackRef.value;

  if (!el) return;

  const currentY = e.touches[0]?.clientY ?? 0;
  const currentX = e.touches[0]?.clientX ?? 0;
  const deltaY = currentY - touchStartY;
  const deltaX = currentX - touchStartX;

  // Só interfere em gesto predominantemente vertical. Sem isso, swipers
  // horizontais aninhados (vistos recentes / mais pagou) ficavam travados
  // porque qualquer jitter vertical no topo do scroll caía no preventDefault.
  if (Math.abs(deltaX) > Math.abs(deltaY)) return;

  const atTop = el.scrollTop <= 0;
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;

  // Puxando pra baixo no topo, ou pra cima no fim → cancela pra não esticar.
  if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
    e.preventDefault();
  }
}

watch(
  [isPreviewOpen, stackRef],
  ([open, el]) => {
    if (!el) return;
    if (open) {
      el.addEventListener('touchstart', onTouchStart, { passive: true });
      el.addEventListener('touchmove', onTouchMove, { passive: false });
      watchActions();
    } else {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      stopWatchingActions();
      // Reabrir o preview começa do topo: sem isto a barra apareceria de cara
      // no próximo jogo, com o botão original ainda visível logo acima.
      isPlayOutOfView.value = false;
    }
  },
  { flush: 'post' },
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isPreviewOpen"
      class="mgp-overlay"
      @click.self="closePreview"
    >
      <div
        class="mgp-backdrop"
        @click="closePreview"
      />

      <Transition
        appear
        name="slide-up"
      >
        <div
          v-if="isPreviewOpen && selectedGame"
          ref="stackRef"
          class="mgp-stack"
          :class="{ 'mgp-stack--float-room': crashGameName }"
        >
          <!-- Thumb fora do sheet pra poder sair acima do modal -->
          <div class="mgp-thumb-wrapper">
            <div class="mgp-thumb">
              <CasinoMediaApostatudo
                :game="selectedGame"
                no-link
              />
            </div>
          </div>

          <div class="mgp-sheet">
            <!-- Topbar: menu + close -->
            <header class="mgp-topbar">
              <CasinoGameActionsMenu
                :game="selectedGame"
                share-only
              />

              <button
                aria-label="Fechar"
                class="mgp-icon-btn"
                type="button"
                @click="closePreview"
              >
                <svg
                  fill="none"
                  height="10"
                  viewBox="0 0 14 14"
                  width="10"
                >
                  <path
                    d="M1 1L13 13M13 1L1 13"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
            </header>

            <!-- Título + provedora -->
            <div class="mgp-title-block">
              <h2 class="mgp-name">{{ selectedGame.name }}</h2>
              <p class="mgp-provider">
                {{ selectedGame.provider?.name || '' }}
              </p>
            </div>

            <!-- Pagou hoje (crash games) — oculto enquanto o core manda null -->
            <CasinoGameCrashPaidToday
              v-if="crashGameName"
              :game-name="crashGameName"
            />

            <!-- JOGAR / JOGAR COM BÔNUS. O wrapper existe pra ser o alvo do
                 observer: é a saída DELE de vista que chama a barra flutuante,
                 então ele envolve as duas ações e nada além disso — saldo e
                 rollover ficam de fora pra não atrasar a troca. -->
            <div ref="actionsRef">
              <button
                class="mgp-play-btn"
                :disabled="isChoiceLoading"
                type="button"
                @click="playGame(WALLET_TYPE.REAL)"
              >
                <span
                  v-if="isChoiceLoading"
                  aria-hidden="true"
                  class="mgp-btn-spinner"
                />

                <svg
                  v-else
                  class="mgp-play-icon"
                  fill="none"
                  height="13"
                  viewBox="0 0 11 13"
                  width="11"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 6.5L0 0V13L11 6.5Z"
                    fill="currentColor"
                  />
                </svg>
                JOGAR
              </button>

              <button
                v-if="showBonusOption"
                class="mgp-play-btn mgp-play-btn--bonus"
                :disabled="isChoiceLoading"
                type="button"
                @click="playGame(WALLET_TYPE.BONUS)"
              >
                <svg
                  class="mgp-bonus-icon"
                  fill="none"
                  height="14"
                  viewBox="0 0 14 14"
                  width="14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 6.5h12v6.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5V6.5ZM.5 4h13a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-.5.5H.5A.5.5 0 0 1 0 6V4.5A.5.5 0 0 1 .5 4ZM7 4v9.5M7 4S5.8 1 4.2 1a1.7 1.7 0 0 0 0 3.4M7 4s1.2-3 2.8-3a1.7 1.7 0 0 1 0 3.4"
                    stroke="currentColor"
                    stroke-linejoin="round"
                    stroke-width="1.2"
                  />
                </svg>
                JOGAR COM BÔNUS
              </button>
            </div>

            <template v-if="showBonusOption">
              <!-- Saldo + regra de rollover sao obrigatorios pelo PRD
                   (RF-02/RF-03) antes de iniciar a partida com bonus. -->
              <p class="mgp-bonus-balance">
                Saldo bônus disponível:
                <strong>{{ walletBonusFormatted }}</strong>
              </p>

              <p
                v-if="rollover.summary"
                class="mgp-bonus-rollover"
              >
                {{ rollover.summary }}
              </p>

              <p
                v-if="bonusExpiry.label"
                class="mgp-bonus-rollover"
              >
                Válido até {{ bonusExpiry.label }}
              </p>
            </template>

            <!-- Chips de tags -->
            <CasinoGameTagChips
              class="mgp-chips-wrapper"
              :reset-key="currentGameId"
              :tags="selectedGame.tags"
            />

            <!-- Tela de pre-jogo dos crash games, logo abaixo do bloco do
                 jogo -->
            <CasinoGameCrashPreGame
              v-if="crashGameName"
              class="mgp-crash-history"
              :game-name="crashGameName"
            />

            <!-- Estatísticas — só logado; recebe dados via prop (parent coordena fetch) -->
            <CasinoGameStatisticsCarousel
              v-if="isAuthenticated"
              class="mgp-statistics"
              :is-loading="isStatisticsLoading"
              :statistics="statistics"
            />

            <!-- Vistos recentes (só logado) — acima do mais pagou -->
            <CasinoGameLastPlayedPreview
              :exclude-slug="selectedGame?.slug"
              :per-row="[5.3, 4.25, 3.65]"
              :space-between="[12, 10, 8]"
              @see-more="closePreview"
            />

            <!-- Mais pagou — recebe dados via prop (parent coordena fetch) -->
            <PerfilOficialMaisPagouApostatudo
              class="mgp-most-paid"
              :class="{ 'mgp-most-paid--spaced': !isAuthenticated }"
              :exclude-slug="selectedGame?.slug"
              :external-loading="isMostPaidLoading"
              :games="mostPaidGames"
              :per-row="[5.3, 4.25, 3.65]"
              :space-between="[12, 10, 8]"
              @see-more="closePreview"
            />

            <!-- Compliance oficial (ENG-404): +18 + advertência + operador -->
            <BaseComplianceInfo class="mgp-compliance" />

            <div class="mgp-safe-bottom" />
          </div>
        </div>
      </Transition>

      <!-- Barra flutuante: o JOGAR volta pro rodapé quando o original já saiu
           de tela. Fica FORA do .mgp-stack de propósito — dentro dele, rolaria
           junto e não resolveria nada. -->
      <div
        v-if="showFloatingPlay"
        class="mgp-float-bar"
      >
        <button
          class="mgp-play-btn"
          :disabled="isChoiceLoading"
          type="button"
          @click="playGame(WALLET_TYPE.REAL)"
        >
          <span
            v-if="isChoiceLoading"
            aria-hidden="true"
            class="mgp-btn-spinner"
          />

          <svg
            v-else
            class="mgp-play-icon"
            fill="none"
            height="13"
            viewBox="0 0 11 13"
            width="11"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11 6.5L0 0V13L11 6.5Z"
              fill="currentColor"
            />
          </svg>
          JOGAR
        </button>

        <!-- Com escolha de carteira a barra leva as DUAS: mandar quem tem bônus
             rolar de volta pra cima pra escolher seria pior que não ter barra.
             Saldo e rollover ficam no bloco lá em cima, que é onde há espaço
             pra ler antes de decidir. -->
        <button
          v-if="showBonusOption"
          class="mgp-play-btn mgp-play-btn--bonus"
          :disabled="isChoiceLoading"
          type="button"
          @click="playGame(WALLET_TYPE.BONUS)"
        >
          <svg
            class="mgp-bonus-icon"
            fill="none"
            height="14"
            viewBox="0 0 14 14"
            width="14"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 6.5h12v6.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5V6.5ZM.5 4h13a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-.5.5H.5A.5.5 0 0 1 0 6V4.5A.5.5 0 0 1 .5 4ZM7 4v9.5M7 4S5.8 1 4.2 1a1.7 1.7 0 0 0 0 3.4M7 4s1.2-3 2.8-3a1.7 1.7 0 0 1 0 3.4"
              stroke="currentColor"
              stroke-linejoin="round"
              stroke-width="1.2"
            />
          </svg>
          BÔNUS
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style>
/* Transições "fade-overlay" e "slide-up" — também declaradas em
   assets/css/main.scss (globais). Replicadas aqui sem scope pra garantir
   que o Vue Transition aplique mesmo se o SCSS global não recarregar. */
.fade-overlay-enter-active,
.fade-overlay-leave-active {
  transition: opacity 0.25s ease;
}
.fade-overlay-enter-from,
.fade-overlay-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  animation: slide-up-spring 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.slide-up-leave-active {
  animation: slide-up-spring 0.22s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes slide-up-spring {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped>
.mgp-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.mgp-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.mgp-stack {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  /* Margem de segurança no topo: garante que o thumb nunca cole no
     topo da tela (respeita notch/status-bar). Combinado com o cap em
     100dvh, força o conteúdo a iniciar abaixo dessa folga. */
  padding-top: calc(env(safe-area-inset-top, 0px) + 24px);
  /* Cap em 100dvh; o scroll fica AQUI (não no .mgp-sheet) pra que o
     thumb também role pra fora do viewport quando o usuário scrolla.
     Antes, com overflow no sheet, o thumb (irmão fora do scroll) ficava
     sticky por cima. */
  max-height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  /* Sem -webkit-overflow-scrolling: touch — ele força momentum legacy que
     habilita o rubber-band. iOS 13+ tem momentum nativo sem ele. */
  overscroll-behavior: none;
}

/* Espaço pro rodapé flutuante não cobrir o fim do conteúdo — o aviso de
   compliance mora ali embaixo. Reservado o tempo todo (e não só com a barra em
   tela) porque aparecer no meio do scroll empurraria o conteúdo sob o dedo. */
.mgp-stack--float-room .mgp-safe-bottom {
  height: calc(env(safe-area-inset-bottom, 16px) + 64px);
  min-height: 80px;
}

.mgp-sheet {
  position: relative;
  flex: 1 0 auto;
  padding: 50px 16px 0;
  background: linear-gradient(180deg, #0a0f2e 0%, #020716 100%);
  border-radius: 18px 18px 0 0;
}

/* Topbar */
.mgp-topbar {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 5;
}

.mgp-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  border: none;
  cursor: pointer;
}

.mgp-icon-btn:hover {
  background: rgba(255, 255, 255, 0.16);
}

/* Thumb elevada — irmã do sheet, fica acima e o sheet sobrepõe a parte inferior */
.mgp-thumb-wrapper {
  position: relative;
  z-index: 11;
  display: flex;
  justify-content: center;
  margin-bottom: -50px;
  pointer-events: none;
}

.mgp-thumb {
  width: 110px;
  aspect-ratio: 6 / 7;
  border-radius: 12px;
  position: relative;
  pointer-events: auto;
}

/* Título */
.mgp-title-block {
  text-align: center;
  padding: 10px 4px;
  user-select: none;
  -webkit-user-select: none;
}

.mgp-name {
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 4px;
}

.mgp-provider {
  color: #98a2b3;
  font-size: 13px;
  font-weight: 500;
  margin: 0;
}

/* JOGAR */
.mgp-play-btn {
  position: relative;
  overflow: hidden;
  display: flex;
  width: 100%;
  height: 35px;
  padding: 0 10px;
  justify-content: center;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  border-radius: 6px;
  border: 1px solid #fc9984;
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #ff704a 0%,
    #ff3400 100%
  );
  color: #ffffff;
  text-align: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.1s ease;
}

/* Shimmer shine — mesmo efeito do .shinyCard nos queridinhos */
.mgp-play-btn::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -100%;
  width: 50%;
  height: 200%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%
  );
  transform: skewX(-25deg);
  animation: shimmerSlide 1.5s ease-out infinite;
  pointer-events: none;
  z-index: 1;
}

.mgp-play-btn > * {
  position: relative;
  z-index: 2;
}

@keyframes shimmerSlide {
  0% {
    left: -100%;
  }
  100% {
    left: 150%;
  }
}

.mgp-play-btn:hover {
  filter: brightness(1.08);
}

.mgp-play-btn:active {
  transform: scale(0.98);
}

.mgp-play-icon {
  width: 11px;
  height: 13px;
  flex-shrink: 0;
  display: block;
}

/* Aguardando saldo/elegibilidade. Discreto de proposito: o botao continua
   legivel e no mesmo lugar, so nao aceita toque — piscar ou trocar o rotulo
   chamaria mais atencao do que a espera merece. */
.mgp-play-btn:disabled {
  cursor: default;
  opacity: 0.65;
}

.mgp-play-btn:disabled:hover {
  filter: none;
}

.mgp-play-btn:disabled:active {
  transform: none;
}

/* O spinner ocupa a caixa do icone de play, entao o texto nao dança quando um
   vira o outro. `animation` e nao `transition`: o reset global do main.scss
   zera transition no app inteiro. */
.mgp-btn-spinner {
  width: 11px;
  height: 13px;
  flex-shrink: 0;
  display: block;
  position: relative;
}

.mgp-btn-spinner::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 11px;
  height: 11px;
  margin: -5.5px 0 0 -5.5px;
  border: 2px solid rgb(255 255 255 / 35%);
  border-top-color: #fff;
  border-radius: 50%;
  animation: mgp-btn-spin 0.6s linear infinite;
}

@keyframes mgp-btn-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Jogar com Bônus — mesma caixa do botão real, só a cor muda. Verde é o
   sinal combinado com o PRD pra carteira de bônus. */
.mgp-play-btn--bonus {
  margin-top: 8px;
  border-color: #6ee7a8;
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #34d399 0%,
    #059669 100%
  );
}

.mgp-bonus-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  display: block;
}

.mgp-bonus-balance {
  color: #98a2b3;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  margin: 8px 0 0;
}

.mgp-bonus-balance strong {
  color: #34d399;
  font-weight: 700;
}

/* Regra de rollover — informativa, um degrau abaixo do saldo. */
.mgp-bonus-rollover {
  color: #667085;
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  margin: 2px 0 0;
}

/* Chips wrapper — bleed lateral do sheet pra encostar nas bordas, igual antes */
.mgp-chips-wrapper {
  padding: 16px 16px 0;
  margin: 0 -16px;
}

/* Estatísticas */
/* Card de histórico de velas dos crash games (ENG-833): respira do bloco do
   jogo acima, na mesma medida das outras seções do sheet. */
.mgp-crash-history {
  margin-top: 14px;
  /* O componente fecha com um divisor de largura total; sem isto a linha
     encostava no título "Estatísticas", que só traz 12px de padding. */
  margin-bottom: 8px;
}

.mgp-statistics {
  margin-bottom: 8px;
}

/* Mais pagou */
.mgp-most-paid {
  padding-top: 0;
  margin-bottom: 0 !important;
}

/* Deslogado: estatísticas e "vistos recentes" ficam ocultos, então adiciona
   respiro entre os chips/botão e o "mais pagou" pra não colarem. */
.mgp-most-paid--spaced {
  margin-top: 20px;
}

/* Reduz o valor "Pagou hoje" dos cards dentro do preview (vistos recentes
   e mais pagou). Targeta a classe gerada do CSS module do MediaApostatudo. */
.mgp-stack :deep([class*='payTodayValue']) {
  font-size: 8px !important;
}

.mgp-most-paid-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.mgp-most-paid-icon {
  font-size: 22px;
  color: #ffffff;
}

.mgp-most-paid-title {
  flex: 1;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}

.mgp-see-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #00c9ff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
}

/* Compliance — respiro acima pra separar do "mais pagou" */
.mgp-compliance {
  margin-top: 20px;
}

.mgp-safe-bottom {
  height: env(safe-area-inset-bottom, 16px);
  min-height: 16px;
}

/* Barra flutuante do JOGAR (crash games). Ancorada no overlay, que é fixo —
   `position: fixed` aqui dentro brigaria com o teclado virtual e com a barra de
   endereço do iOS. */
.mgp-float-bar {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  /* Acima do .mgp-stack, que é z-index 10. */
  z-index: 11;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(env(safe-area-inset-bottom, 10px) + 10px);
  background: linear-gradient(
    180deg,
    rgba(7, 11, 20, 0),
    rgba(7, 11, 20, 0.92) 38%
  );
  /* @keyframes e não `transition`: o main.scss zera transition no app inteiro. */
  animation: mgp-float-in 180ms ease-out both;
}

/* O botão do sheet é width:100%; na barra os dois dividem a linha. */
.mgp-float-bar .mgp-play-btn {
  flex: 1 1 0;
  min-width: 0;
  height: 44px;
}

@keyframes mgp-float-in {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mgp-float-bar {
    animation: none;
  }
}

/* Transições "fade-overlay" e "slide-up" são globais — assets/css/main.scss */
</style>
